import axios from 'axios'

/**
 * How a request failed when the server gave no typed refusal of its own:
 * - `offline`: the browser reports no network;
 * - `network`: no response arrived (dropped connection, DNS, CORS, a proxy
 *   that closed the socket);
 * - `timeout`: the client gave up waiting;
 * - `session_expired`: 401;
 * - `forbidden`: an uncoded 403 (no access any more);
 * - `not_found`: an uncoded 404;
 * - `too_large`: 413, usually from the proxy in front of the API;
 * - `server`: an uncoded 5xx (a crash, or a gateway that lost the backend);
 * - `cancelled`: the caller aborted the request.
 */
export type TransportFailureKind =
  | 'offline'
  | 'network'
  | 'timeout'
  | 'session_expired'
  | 'forbidden'
  | 'not_found'
  | 'too_large'
  | 'server'
  | 'cancelled'

/**
 * The one transport classification. It reads typed fields only (the axios
 * flag, the error code, the HTTP status, whether the body carries a `code`),
 * never a sentence. Null when the failure is not a transport one: a thrown
 * non-HTTP error, or a response whose body names its own `code` (the rule
 * catalog owns that line) — except 401, which is always the session.
 */
export function classifyTransportFailure(
  error: unknown,
): TransportFailureKind | null {
  if (axios.isCancel(error)) return 'cancelled'
  if (!axios.isAxiosError(error)) return null
  const response = error.response
  if (!response) {
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return 'timeout'
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return 'offline'
    }
    return 'network'
  }
  if (response.status === 401) return 'session_expired'
  const code = (response.data as { code?: unknown } | null | undefined)?.code
  if (typeof code === 'string' && code.length > 0) return null
  if (response.status === 403) return 'forbidden'
  if (response.status === 404) return 'not_found'
  if (response.status === 413) return 'too_large'
  if (response.status >= 500) return 'server'
  return null
}

/**
 * Whether no HTTP answer arrived at all: the browser is offline, the
 * connection dropped, or the client gave up waiting. Any other failure is an
 * answer, typed or not.
 */
export function noAnswerArrived(kind: TransportFailureKind | null): boolean {
  return kind === 'offline' || kind === 'network' || kind === 'timeout'
}

/**
 * Whether the request may have been applied although no answer said so: the
 * caller can't tell success from failure, so it offers Retry (a retry carries
 * the request's own identity: a turn's client id, a Save's revision).
 */
export function transportOutcomeUnknown(kind: TransportFailureKind): boolean {
  return noAnswerArrived(kind) || kind === 'server'
}

/**
 * Whether a failed read a page needs to open is final: anything but no
 * answer or an uncoded 5xx (a restart, a gateway that lost the backend),
 * which the next try may answer. A thrown non-HTTP error is final too, so a
 * bug never spins the page's loader.
 */
export function pageReadFinal(error: unknown): boolean {
  const kind = classifyTransportFailure(error)
  return kind === null || !transportOutcomeUnknown(kind)
}

/**
 * Whether a read was answered in a way no retry changes: the session expired
 * (401), access is gone (403), or what it reads no longer exists (404),
 * coded or not. Any other failed read may answer next time. The line comes
 * from the body's code (the rule catalog) or the transport kind.
 */
export function readRefused(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false
  const status = error.response?.status
  return status === 401 || status === 403 || status === 404
}
